// TravelersPicker.jsx
import React, { useMemo, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./TravelersPicker.module.css";
import { Text, Input, InputNumber, Button } from "@zorni/components-lib";
import { useTranslation } from "react-i18next";

/**
 * Props rétro-compatible :
 * - adult, child, baby, setAdult, setChild, setBaby
 * - showVoyageur, setShowVoyageur, setShowDatePicker
 * - isFilter (position relative/absolute)
 * - clearable : affiche un ✕ pour remettre les voyageurs à zéro (formulaires
 *   de recherche). Désactivé par défaut : le récapitulatif de réservation
 *   impose au moins un adulte.
 */
const TravelersPicker = ({
  floatingLabel = true,
  appearance = "outlined",
  isFilter,
  adult = 0,
  child = 0,
  baby = 0,
  setAdult,
  setChild,
  setBaby,
  showVoyageur,
  setShowVoyageur,
  setShowDatePicker,
  showEmptyPreview = true,
  placeholder,
  beforeOpen,
  clearable = false,
}) => {
  const { t } = useTranslation();
  const anchorRef = useRef(null);

  const total =
    (Number(adult) || 0) + (Number(child) || 0) + (Number(baby) || 0);

  // ✅ On utilise les clés i18n "traveler.*" (cohérentes dans EN/FR)
  const preview = useMemo(() => {
    if (total === 0) return t("Traveler.AddTravelers") || t("traveler.title"); // fallback safe

    const parts = [];
    if (adult)
      parts.push(
        `${adult} ${t("traveler.content.type1")}${Number(adult) > 1 ? "s" : ""}`
      );
    if (child)
      parts.push(
        `${child} ${t("traveler.content.type2")}${Number(child) > 1 ? "s" : ""}`
      );
    if (baby)
      parts.push(
        `${baby} ${t("traveler.content.type3")}${Number(baby) > 1 ? "s" : ""}`
      );

    return parts.join(" · ");
  }, [adult, child, baby, total, t]);

  const close = useCallback(() => setShowVoyageur(false), [setShowVoyageur]);

  const canClear = clearable && total > 0;

  const handleClear = (e) => {
    // Input écoute aussi le clic sur son conteneur : sans ça, le ✕ rouvrirait
    // la fenêtre des voyageurs.
    e?.stopPropagation();
    e?.preventDefault();
    setAdult(0);
    setChild(0);
    setBaby(0);
    close();
  };

  const handleSubmit = useCallback(() => {
    const adultsCount = Number(adult) || 0;
    const childrenCount = Number(child) || 0;
    const babiesCount = Number(baby) || 0;

    if (adultsCount === 0 && (childrenCount > 0 || babiesCount > 0)) {
      setAdult(1);
    }

    close();
  }, [adult, child, baby, setAdult, close]);

  const toggle = () => {
    if (!showVoyageur) {
      const delay = typeof beforeOpen === "function" ? Number(beforeOpen()) || 0 : 0;

      if (delay > 0) {
        window.setTimeout(() => {
          setShowVoyageur(true);
        }, delay);
      } else {
        setShowVoyageur(true);
      }
    } else {
      setShowVoyageur(false);
    }

    setShowDatePicker(false);
  };

  // click hors composant + ESC => referme
  useEffect(() => {
    const onDown = (e) => {
      if (!anchorRef.current) return;
      if (!anchorRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [close]);

  // ===== Custom content (label + preview) =====
  return (
    <div className={styles.wrapper} ref={anchorRef}>
      <Input
        readOnly
        label={t("traveler.title")}
        floatingLabel={floatingLabel}
        appearance={appearance}
        inputSize={isFilter ? "small" : "medium"}
        iconLeft="iconUsers"
        iconRight={canClear ? "iconXCircle" : undefined}
        onRightIconClick={canClear ? handleClear : undefined}
        value={total > 0 ? preview : ""}
        placeholder={
          showEmptyPreview
            ? placeholder || t("Traveler.AddTravelers") || "Ajouter des voyageurs"
            : ""
        }
        onClick={toggle}
        className={styles.inputOverride}
      />

      {showVoyageur && (
        <div
          className={`${styles.popover} ${isFilter ? styles.relative : styles.absolute}`}
          role="dialog"
          aria-label={t("traveler.title")}
        >
          <div className={styles.rows}>
            <PickerRow
              title={t("traveler.content.type1")}
              subtitle={t("traveler.condition.type1")}
              value={adult}
              onChange={setAdult}
            />
            <PickerRow
              title={t("traveler.content.type2")}
              subtitle={t("traveler.condition.type2")}
              value={child}
              onChange={setChild}
            />
            <PickerRow
              title={t("traveler.content.type3")}
              subtitle={t("traveler.condition.type3")}
              value={baby}
              onChange={setBaby}
            />
          </div>

          <div className={styles.footer}>
            {/* ✅ Button de components-lib => on passe label i18n (pas de children) */}
            <Button
              variant="ghost"
              size="small"
              label={t("commons.allActions.reset")}
              onClick={() => {
                setAdult(0);
                setChild(0);
                setBaby(0);
              }}
            />

            <Button
              variant="primary"
              size="small"
              label={t("commons.buttons.submit")}
              onClick={handleSubmit}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const PickerRow = ({ title, subtitle, value = 0, onChange }) => {
  return (
    <div className={styles.row}>
      <div className={styles.rowText}>
        <Text as="div" size="md" weight="medium">
          {title}
        </Text>
        <Text as="div" size="xs" color="subtle">
          {subtitle}
        </Text>
      </div>

      <div className={styles.rowCtrls}>
        <InputNumber
          inputSize="small"
          min={0}
          max={20}
          value={Number(value) || 0}
          onClick={(v) => onChange(Number(v))}
        />
      </div>
    </div>
  );
};

TravelersPicker.propTypes = {
  isFilter: PropTypes.bool,
  adult: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  child: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  baby: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setAdult: PropTypes.func.isRequired,
  setChild: PropTypes.func.isRequired,
  setBaby: PropTypes.func.isRequired,
  showVoyageur: PropTypes.bool.isRequired,
  setShowVoyageur: PropTypes.func.isRequired,
  setShowDatePicker: PropTypes.func.isRequired,
  showEmptyPreview: PropTypes.bool,
  placeholder: PropTypes.string,
  beforeOpen: PropTypes.func,
  clearable: PropTypes.bool,
};

PickerRow.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
};

export default TravelersPicker;
